/**
 * Design tokens — ported verbatim from opus5.5aesthetic.jsx §1 (lines 62–143).
 * Owner: S1. Raw hex values may appear only here and in src/map/style/moods.ts.
 *
 * The same values are written out as CSS custom properties in
 * src/design/css/base.css (on :root and on .ld). `tokenCssVars()` below is the
 * generator the original used (ROOT_VARS); keep the two in step.
 */
export const tokens = {
  color: {
    // Brand book, "Colour Option – Combined"
    ivory: "#F4E8DA",
    stone: "#DACEBF",
    sand: "#DEB98F",
    sun: "#F2A905",
    terracotta: "#C0552C",
    mud: "#845730",
    earth: "#5E3B28",
    sage: "#788361",
    olive: "#7F8A60",
    sky: "#92ADA4",
    forest: "#3A4530",
    charcoal: "#252525",
    // Derived for screens. Not in the brand book; tuned for contrast.
    soil: "#2B1D14", //          base of dusk glass; text on Sun buttons (8.1:1)
    night: "#17110C", //         scrims, loader, the stage before first light
    ochre: "#A67C3D", //         lower half of the brand mark (sampled)
    sunSoft: "#F6C75A", //       focus ring and small sun marks on dark
    sageInk: "#55603D", //       "available" text on paper (5.6:1)
    terracottaInk: "#9A4320", // "sold" text on paper (5.4:1)
    sageLit: "#A9B38B", //       "available" text on glass (7.4:1)
    terracottaLit: "#E08A63", // "sold" text on glass (6.2:1)
    skyLit: "#B4CBC3", //        water labels, "next phase" on glass
  },
  roles: {
    paper: "ivory",
    ink: "earth",
    inkSecondary: "mud",
    light: "sun",
    lightSecondary: "sand",
    glass: "soil",
    available: "sage",
    limited: "sand",
    sold: "terracotta",
    nextPhase: "sky",
    water: "sky",
    greenCorridor: "sage",
  },
  type: {
    // Self-hosted Newsreader (@fontsource-variable, family "Newsreader Variable", opsz 6–72)
    // stands in until the IvyPresto Adobe kit exists. See src/design/fonts.ts.
    display: '"ivypresto-headline", "IvyPresto Headline", "Newsreader Variable", "Newsreader", Georgia, serif',
    text: '"Open Sans", system-ui, -apple-system, "Segoe UI", sans-serif',
    devaDisplay: '"Tiro Devanagari Marathi", "Noto Serif Devanagari", serif',
    devaText: '"Noto Sans Devanagari", sans-serif',
    // Brand book hierarchy is set at 1920 px: H1 90/85, H2 60/55, Subhead 35/38, Body 25/32.
    // Screen scale keeps the brand's ratios and tight leading, fluid between 1280 and 1920.
    scale: [
      { token: "hero", size: "clamp(52px, 6.6vw, 124px)", px: 124, lh: 0.9, track: "-0.045em", family: "display", sample: "The land was here first." },
      { token: "h1", size: "clamp(44px, 4.7vw, 90px)", px: 90, lh: 0.944, track: "-0.04em", family: "display", sample: "Everything else followed." },
      { token: "h2", size: "clamp(34px, 3.2vw, 60px)", px: 60, lh: 0.917, track: "-0.035em", family: "display", sample: "Life happens between buildings." },
      { token: "h3", size: "clamp(24px, 1.9vw, 34px)", px: 34, lh: 1.0, track: "-0.025em", family: "display", sample: "Ravine Walk" },
      { token: "fig", size: "clamp(26px, 2.1vw, 40px)", px: 40, lh: 1.0, track: "-0.03em", family: "display", sample: "1,184 sq ft" },
      { token: "sub", size: "clamp(17px, 1.25vw, 21px)", px: 21, lh: 1.4, track: "-0.005em", family: "text", sample: "Homes along the ravine edge, where the morning shade lasts longest." },
      { token: "body", size: "15px", px: 15, lh: 1.6, track: "0", family: "text", sample: "Every street, pathway and park is part of one system. The width of a boulevard, the shade of a canopy, the distance to the nearest bench." },
      { token: "label", size: "12.5px", px: 12.5, lh: 1.2, track: "0.01em", family: "text", weight: 600, sample: "Explore homes" },
      { token: "meta", size: "11px", px: 11, lh: 1.2, track: "0.14em", family: "text", weight: 600, caps: true, sample: "594 m above sea level" },
    ],
  },
  space: [4, 8, 12, 16, 20, 24, 32, 40, 56, 72, 96, 128],
  radius: { pill: 999, sheet: 28, card: 22, tile: 14, inner: 8 }, // inner = outer − gap
  light: {
    rest: "0 0 0 1px rgba(242,169,5,.55), 0 0 28px -6px rgba(242,169,5,.55)",
    arriving: "0 0 0 1px rgba(242,169,5,.30), 0 0 22px -8px rgba(242,169,5,.40)",
    sunButton: "inset 0 1px 0 rgba(255,255,255,.35), 0 12px 28px -14px rgba(242,169,5,.9)",
  },
  elevation: {
    e1: "0 1px 0 rgba(94,59,40,.05), 0 1px 3px rgba(94,59,40,.10)",
    e2: "0 18px 40px -22px rgba(94,59,40,.45), 0 4px 10px -6px rgba(94,59,40,.18)",
    e3: "0 30px 80px -30px rgba(23,17,12,.75), 0 10px 30px -18px rgba(23,17,12,.6)",
  },
  motion: {
    ease: {
      settle: "cubic-bezier(.22,1,.36,1)", //   arrivals: panels, cards, indicators
      drift: "cubic-bezier(.45,0,.2,1)", //     camera moves, scene cross-fades, reveals
      leave: "cubic-bezier(.4,0,1,1)", //       exits accelerate away
      breathe: "cubic-bezier(.37,0,.63,1)", //  ambient loops only
    },
    duration: { instant: 120, quick: 240, calm: 420, unhurried: 640, scene: 1100, ambient: 9000 },
    stagger: 40,
  },
} as const;

export type Tokens = typeof tokens;
export type ColorToken = keyof typeof tokens.color;

/* ─── CSS custom-property helpers ─────────────────────────────────────────── */

/** camelCase → kebab-case at the type level: "sunSoft" → "sun-soft". */
type Kebab<S extends string> = S extends `${infer H}${infer R}` ? `${H extends Lowercase<H> ? H : `-${Lowercase<H>}`}${Kebab<R>}` : S;

/** Custom properties generated from the tokens (the original's ROOT_VARS). */
export type TokenCssVar =
  | `--${Kebab<ColorToken>}`
  | `--ease-${keyof Tokens["motion"]["ease"]}`
  | `--d-${keyof Tokens["motion"]["duration"]}`
  | `--${keyof Tokens["elevation"]}`
  | `--r-${keyof Tokens["radius"]}`
  | "--f-display"
  | "--f-text"
  | "--f-deva-display"
  | "--f-deva-text";

/** Every custom property the design system defines: the tokens plus --ridge (set by ensureRidge) and --focus. */
export type CssVarName = TokenCssVar | "--ridge" | "--focus";

const kebab = (s: string): string => s.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());

/** `var(--name)`, or `var(--name, fallback)`, for a design-system custom property. */
export function cssVar(name: CssVarName, fallback?: string): string {
  return fallback === undefined ? `var(${name})` : `var(${name}, ${fallback})`;
}

/** The CSS variable for a colour token: colorVar("sunSoft") → "var(--sun-soft)". */
export function colorVar(token: ColorToken): string {
  return `var(--${kebab(token)})`;
}

/**
 * The token custom properties in the original ROOT_VARS order:
 * colours, --ease-*, --d-* (ms), --e1..3, --r-* (px), --f-*.
 * base.css carries the same list as literal CSS.
 */
export function tokenCssVars(): Record<TokenCssVar, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(tokens.color)) out[`--${kebab(k)}`] = v;
  for (const [k, v] of Object.entries(tokens.motion.ease)) out[`--ease-${k}`] = v;
  for (const [k, v] of Object.entries(tokens.motion.duration)) out[`--d-${k}`] = `${v}ms`;
  for (const [k, v] of Object.entries(tokens.elevation)) out[`--${k}`] = v;
  for (const [k, v] of Object.entries(tokens.radius)) out[`--r-${k}`] = `${v}px`;
  out["--f-display"] = tokens.type.display;
  out["--f-text"] = tokens.type.text;
  out["--f-deva-display"] = tokens.type.devaDisplay;
  out["--f-deva-text"] = tokens.type.devaText;
  return out as Record<TokenCssVar, string>;
}

/** The resolved value of a custom property on an element (default: the document root). "" outside the browser. */
export function readCssVar(name: CssVarName, el?: Element): string {
  if (typeof document === "undefined" || typeof getComputedStyle === "undefined") return "";
  return getComputedStyle(el ?? document.documentElement).getPropertyValue(name).trim();
}
